<template>
  <div class="form-group position-relative"
       :class="{ hasErrors }">
    <label class="form-label"
           :class="labelclass"
           v-if="showlabel"
           :data-content="label">
      <span class="">{{ label }}</span>
      <span v-if="required"> *</span>
    </label>
    <div v-if="editMode"
         class="position-relative">
    <textarea class="form-control showplaceholder"
              :class="{
                'form-control-white': white,
                'enabled-dictation': dictation,
                hasSaveBtn
              }"
              :placeholder="placeholder"
              :style="inputStyle"
              :value="modelValue"
              ref="autotext"
              @focus="focus"
              :rows="rows"
              :disabled="readonly"
              @keyup.enter.exact="enterPressed"
              @keydown.enter.shift.exact="newline"
              @blur="blur"
              @change="emit('change')"
              @input="handleInput">
        </textarea>
      <div class="helper-buttons">
        <SpeechRecognizer class="speech-recognizer p-0"
                          v-model="v"
                          v-if="dictation"/>
        <slot name="action-button"/>
      </div>
      <textarea class="shadowtextarea"
                v-model="shadowcontent"
                ref="shadow"
                tabindex="0">
        </textarea>
      <div v-if="maxLength > 0"
           class="small text-muted text-right">
        {{ modelValue ? modelValue.length : 0 }} / {{ maxLength }}
      </div>
      <ValidationError :class="[{ 'mt-1': !validationClass }, validationClass]"
                       v-if="showErrors"
                       :validationId="validationId"/>
    </div>
    <div v-if="!editMode">{{ modelValue || '-' }}</div>
  </div>
</template>

<script setup>
import SpeechRecognizer from "@/components/form/SpeechRecognizer"
import ValidationError from "@/components/form/ValidationError"
import {computed, nextTick, onMounted, ref, watch} from "vue"
import {useStore} from "vuex"

const store = useStore()

const emit = defineEmits(['update:modelValue', 'focus', 'blur', 'change', 'enter'])

const props = defineProps({
  modelValue: {},
  placeholder: {},
  white: {},
  minH: {},
  maxH: {},
  rows: {
    default: 'auto'
  },
  labelclass: { default: ''},
  label: {},
  dictation: {
    type: Boolean,
    default: false
  },
  showlabel: {
    type: Boolean,
    default: true
  },
  showErrors: {
    type: Boolean,
    default: false
  },
  readonly: {
    type: Boolean,
    default: false
  },
  validationId: {},
  validationClass: {
    type: String,
    default: null
  },
  maxLength: {
    type: Number,
    default: 0
  },
  required: {
    type: Boolean,
    default: false
  },
  hasSaveBtn: {
    type: Boolean,
    default: false
  },
  editMode: {
    type: Boolean,
    default: true
  }
})

const autotext = ref(null)
const shadow = ref(null)
const shadowcontent = ref(props.modelValue)
const v = ref(props.modelValue)
const inputHeight = ref('0')
const minHeight = ref(60)
const maxHeight = ref(110)

const errors = computed(() => store.state.datatable.validationErrors)
const inputStyle = computed(() => ({
  'min-height': inputHeight.value
}))
const hasErrors = computed(() => {
  if(!props.showErrors || !props.validationId) return false
  return !!errors.value[props.validationId]
})

onMounted(() => {
  if(props.minH) {
    minHeight.value = props.minH
  }
  if(props.maxH) {
    maxHeight.value = props.maxH
  }
  resize(props.modelValue)
  setTimeout(() => {
    resize(props.modelValue)
  },20)
})

watch(() => props.modelValue, (val) => {
  resize(val)
  v.value = val
})

watch(v, (val) => {
  emit('update:modelValue', val)
})

const handleInput = (e) => {
  emit('update:modelValue', e.target.value)
}

const newline = () => {
  emit('update:modelValue', `${props.modelValue}`)
}

const focusCustom = () => {
  autotext.value.focus()
}

const resize = (val) => {
  shadowcontent.value = val
  nextTick(() => {
    let h = Math.max(Math.min(shadow.value ? shadow.value.scrollHeight : minHeight.value, maxHeight.value), minHeight.value)
    if(val && (shadow.value?.scrollHeight || 0) === 0) {
      h = calculateHeightProgramatically(val)
    }
    if(h > 46) { h += 20 }
    inputHeight.value = `${h}px`
    if(val == '') {
      inputHeight.value = minHeight.value+"px"
    }
  })
}

const focus = () => {
  emit('focus')
}

const blur = () => {
  emit('blur')
}

const enterPressed = () => {
  emit('enter')
}

const calculateHeightProgramatically = (val) => {
  const element = document.createElement("div")
  element.innerHTML = val
  element.style.position = 'absolute'
  element.style.visibility = 'hidden' // Keep it hidden
  element.style.display = 'block' // Make it block-level
  element.style.whiteSpace = 'pre-wrap' // Ensure wrapping if necessary
  document.body.appendChild(element)
  let textHeight = element.offsetHeight
  document.body.removeChild(element)
  return textHeight
}

defineExpose({
  focusCustom,
})
</script>

<style scoped lang="scss">
.helper-buttons {
  position: absolute;
  right: 4px;
  top: 0;
  display: flex;
  flex-direction: row;
}
textarea {
  resize: none;
  height: 0;
  &.shadowtextarea {
    max-height: 0;
    pointer-events: none;
    opacity: 0;
    margin: 0;
    padding-left: 15px;
    padding-right: 46px;
    position: absolute;
    width: 100%;
  }
  &.white {
    background: #fff;

  }
}
@media screen and (max-width: 768px) {
  textarea {
    &.shadowtextarea {
      padding-left: 12px;
    }
  }
}
</style>
